import { PropertyResponse } from '@/services/api'
import { getRenovationCosts, hasRenovationCosts, getRenovationCostEntries, safeNumber } from './propertyCalculations'

// Utility function to format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Utility function to format percentage
const formatPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

// Utility function to format date
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Calculate monthly mortgage payment
const calculateMonthlyPayment = (loanAmount: number, annualInterestRate: number, loanTermYears: number): number => {
  const monthlyInterestRate = annualInterestRate / 100 / 12;
  const numberOfPayments = loanTermYears * 12;
  
  if (monthlyInterestRate === 0) {
    return loanAmount / numberOfPayments;
  }
  
  const monthlyPayment = loanAmount * 
    (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) / 
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
  
  return Math.round(monthlyPayment);
};

// Calculate property metrics
const calculatePropertyMetrics = (property: PropertyResponse) => {
  const totalRenovationCosts = getRenovationCosts(property.renovation_costs)
  const totalInvestment = safeNumber(property.purchase_price) + totalRenovationCosts
  
  const monthlyMortgage = calculateMonthlyPayment(
    property.loan_amount,
    property.interest_rate,
    property.loan_term
  );
  
  const monthlyExpenses = monthlyMortgage + 
    (property.annual_taxes / 12) + 
    (property.annual_insurance / 12) + 
    (property.monthly_hoa_fees || 0);

  const monthlyCashFlow = (property.expected_monthly_rent || 0) - monthlyExpenses;
  const annualCashFlow = monthlyCashFlow * 12;
  const cashOnCashReturn = property.down_payment > 0 
    ? (annualCashFlow / property.down_payment) * 100 
    : 0;

  const noi = (property.expected_monthly_rent || 0) * 12 - (property.annual_taxes + property.annual_insurance);
  const capRate = property.purchase_price > 0 ? (noi / property.purchase_price) * 100 : 0;

  return {
    totalInvestment,
    totalRenovationCosts,
    monthlyMortgage,
    monthlyExpenses,
    monthlyCashFlow,
    annualCashFlow,
    cashOnCashReturn,
    capRate,
    noi
  };
};

// Generate CSV content for portfolio export
export const generatePortfolioCSV = (properties: PropertyResponse[]): string => {
  const headers = [
    'Property Address',
    'City',
    'State',
    'ZIP Code',
    'Analysis Type',
    'Property Type',
    'Purchase Price',
    'Down Payment',
    'Loan Amount',
    'Interest Rate (%)',
    'Loan Term (Years)',
    'Annual Taxes',
    'Annual Insurance',
    'Monthly HOA',
    'Expected Monthly Rent',
    'Vacancy Rate (%)',
    'Maintenance Rate (%)',
    'Management Rate (%)',
    'After Repair Value',
    'Holding Period (Months)',
    'Selling Costs',
    'Renovation - Roof',
    'Renovation - Floors',
    'Renovation - Painting',
    'Renovation - General',
    'Total Renovation Costs',
    'Monthly Cash Flow',
    'Annual Cash Flow',
    'Cash-on-Cash Return (%)',
    'Cap Rate (%)',
    'Date Added'
  ];

  const csvRows = [headers.join(',')];

  properties.forEach(property => {
    const metrics = calculatePropertyMetrics(property);
    const analysisType = property.analysis_status === 'buy-hold' ? 'Buy & Hold' :
                        property.analysis_status === 'flip' ? 'Fix & Flip' :
                        property.analysis_status === 'brrr' ? 'BRRR' : 'Analysis';

    // Helper function to escape CSV values
    const escapeCSV = (value: string | number | null | undefined): string => {
      if (value === null || value === undefined) return '""';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Extract individual renovation costs
    const renovationCosts = property.renovation_costs || {};
    const roofCost = renovationCosts.roof || renovationCosts.new_roof || 0;
    const floorsCost = renovationCosts.floors || renovationCosts.flooring || renovationCosts.new_floors || 0;
    const paintingCost = renovationCosts.painting || 0;
    const generalCost = renovationCosts.general || renovationCosts.general_fixup || renovationCosts.fixup || 0;

    const row = [
      escapeCSV(property.property_address),
      escapeCSV(property.city || ''),
      escapeCSV(property.state || ''),
      escapeCSV(property.zip_code || ''),
      escapeCSV(analysisType),
      escapeCSV(property.property_type?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || ''),
      property.purchase_price,
      property.down_payment,
      property.loan_amount,
      property.interest_rate,
      property.loan_term,
      property.annual_taxes,
      property.annual_insurance,
      property.monthly_hoa_fees || 0,
      property.expected_monthly_rent || 0,
      property.vacancy_rate || 0,
      property.maintenance_rate || 0,
      property.management_rate || 0,
      property.after_repair_value || 0,
      property.holding_period_months || 0,
      property.selling_costs || 0,
      roofCost,
      floorsCost,
      paintingCost,
      generalCost,
      metrics.totalRenovationCosts,
      Math.round(metrics.monthlyCashFlow),
      Math.round(metrics.annualCashFlow),
      metrics.cashOnCashReturn.toFixed(2),
      metrics.capRate.toFixed(2),
      escapeCSV(formatDate(property.created_at))
    ];

    csvRows.push(row.join(','));
  });

  return csvRows.join('\n');
};

// Generate detailed property report (HTML for print)
export const generatePropertyReportHTML = (property: PropertyResponse): string => {
  const metrics = calculatePropertyMetrics(property);
  const analysisType = property.analysis_status === 'buy-hold' ? 'Buy & Hold' :
                      property.analysis_status === 'flip' ? 'Fix & Flip' :
                      property.analysis_status === 'brrr' ? 'BRRR' : 'Analysis';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Property Analysis Report - ${property.property_address}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; color: #333; }
        .header { border-bottom: 2px solid #e5e5e5; padding-bottom: 20px; margin-bottom: 30px; }
        .property-title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
        .property-address { font-size: 18px; color: #666; margin-bottom: 5px; }
        .analysis-type { display: inline-block; background: #f0f0f0; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: bold; }
        .section { margin-bottom: 30px; }
        .section-title { font-size: 18px; font-weight: bold; margin-bottom: 15px; padding-bottom: 5px; border-bottom: 1px solid #e5e5e5; }
        .metrics-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 20px; }
        .metric-card { background: #f8f9fa; padding: 15px; border-radius: 8px; }
        .metric-label { font-size: 12px; color: #666; text-transform: uppercase; font-weight: bold; }
        .metric-value { font-size: 20px; font-weight: bold; color: #333; margin-top: 5px; }
        .positive { color: #059669; }
        .negative { color: #dc2626; }
        .details-table { width: 100%; border-collapse: collapse; margin-top: 15px; }
        .details-table th, .details-table td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #e5e5e5; }
        .details-table th { background: #f8f9fa; font-weight: bold; }
        .renovation-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 15px; }
        .renovation-item { background: #f8f9fa; padding: 10px; border-radius: 6px; text-align: center; }
        .renovation-category { font-size: 12px; color: #666; margin-bottom: 5px; }
        .renovation-cost { font-size: 16px; font-weight: bold; color: #333; }
        .cash-flow-breakdown { background: #f8f9fa; padding: 20px; border-radius: 8px; }
        .cash-flow-item { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e5e5; }
        .cash-flow-total { display: flex; justify-content: space-between; padding: 12px 0; font-weight: bold; font-size: 16px; border-top: 2px solid #333; margin-top: 10px; }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="property-title">${property.property_address}</div>
        <div class="property-address">${property.city ? `${property.city}, ` : ''}${property.state || ''} ${property.zip_code || ''}</div>
        <div style="margin-top: 10px;">
          <span class="analysis-type">${analysisType}</span>
          ${property.property_type ? `<span class="analysis-type" style="margin-left: 10px;">${property.property_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>` : ''}
        </div>
        <div style="margin-top: 15px; color: #666; font-size: 14px;">
          Report Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      <div class="section">
        <div class="section-title">Investment Summary</div>
        <div class="metrics-grid">
          <div class="metric-card">
            <div class="metric-label">Total Investment</div>
            <div class="metric-value">${formatCurrency(metrics.totalInvestment)}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Monthly Cash Flow</div>
            <div class="metric-value ${metrics.monthlyCashFlow >= 0 ? 'positive' : 'negative'}">${formatCurrency(metrics.monthlyCashFlow)}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Cash-on-Cash Return</div>
            <div class="metric-value">${formatPercentage(metrics.cashOnCashReturn)}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Cap Rate</div>
            <div class="metric-value">${formatPercentage(metrics.capRate)}</div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Property Details</div>
        <table class="details-table">
          <tr><th>Purchase Price</th><td>${formatCurrency(property.purchase_price)}</td></tr>
          <tr><th>Down Payment</th><td>${formatCurrency(property.down_payment)}</td></tr>
          <tr><th>Loan Amount</th><td>${formatCurrency(property.loan_amount)}</td></tr>
          <tr><th>Interest Rate</th><td>${formatPercentage(property.interest_rate)}</td></tr>
          <tr><th>Loan Term</th><td>${property.loan_term} years</td></tr>
          <tr><th>Annual Property Taxes</th><td>${formatCurrency(property.annual_taxes)}</td></tr>
          <tr><th>Annual Insurance</th><td>${formatCurrency(property.annual_insurance)}</td></tr>
          <tr><th>Monthly HOA Fees</th><td>${formatCurrency(property.monthly_hoa_fees || 0)}</td></tr>
          <tr><th>Expected Monthly Rent</th><td>${formatCurrency(property.expected_monthly_rent || 0)}</td></tr>
          ${property.vacancy_rate ? `<tr><th>Vacancy Rate</th><td>${formatPercentage(property.vacancy_rate)}</td></tr>` : ''}
          ${property.maintenance_rate ? `<tr><th>Maintenance Rate</th><td>${formatPercentage(property.maintenance_rate)}</td></tr>` : ''}
          ${property.management_rate ? `<tr><th>Management Rate</th><td>${formatPercentage(property.management_rate)}</td></tr>` : ''}
          ${property.after_repair_value ? `<tr><th>After Repair Value</th><td>${formatCurrency(property.after_repair_value)}</td></tr>` : ''}
          ${property.holding_period_months ? `<tr><th>Holding Period</th><td>${property.holding_period_months} months</td></tr>` : ''}
          ${property.selling_costs ? `<tr><th>Selling Costs</th><td>${formatCurrency(property.selling_costs)}</td></tr>` : ''}
        </table>
      </div>

      <div class="section">
        <div class="section-title">Monthly Cash Flow Analysis</div>
        <div class="cash-flow-breakdown">
          <div class="cash-flow-item">
            <span>Monthly Rental Income:</span>
            <span class="positive">${formatCurrency(property.expected_monthly_rent || 0)}</span>
          </div>
          <div class="cash-flow-item">
            <span>Mortgage Payment:</span>
            <span class="negative">-${formatCurrency(metrics.monthlyMortgage)}</span>
          </div>
          <div class="cash-flow-item">
            <span>Property Taxes (Monthly):</span>
            <span class="negative">-${formatCurrency(Math.round(property.annual_taxes / 12))}</span>
          </div>
          <div class="cash-flow-item">
            <span>Insurance (Monthly):</span>
            <span class="negative">-${formatCurrency(Math.round(property.annual_insurance / 12))}</span>
          </div>
          ${property.monthly_hoa_fees ? `
          <div class="cash-flow-item">
            <span>HOA Fees:</span>
            <span class="negative">-${formatCurrency(property.monthly_hoa_fees)}</span>
          </div>
          ` : ''}
          <div class="cash-flow-total">
            <span>Net Monthly Cash Flow:</span>
            <span class="${metrics.monthlyCashFlow >= 0 ? 'positive' : 'negative'}">${formatCurrency(metrics.monthlyCashFlow)}</span>
          </div>
        </div>
      </div>

      ${hasRenovationCosts(property.renovation_costs) ? `
      <div class="section">
        <div class="section-title">Renovation Costs</div>
        <div class="renovation-grid">
          ${getRenovationCostEntries(property.renovation_costs).map(([category, cost]) => 
            cost > 0 ? `
            <div class="renovation-item">
              <div class="renovation-category">${category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
              <div class="renovation-cost">${formatCurrency(cost)}</div>
            </div>
            ` : ''
          ).join('')}
          <div class="renovation-item" style="background: #e3f2fd; border: 2px solid #1976d2;">
            <div class="renovation-category">Total Renovation</div>
            <div class="renovation-cost" style="color: #1976d2;">${formatCurrency(metrics.totalRenovationCosts)}</div>
          </div>
        </div>
      </div>
      ` : ''}

      <div class="section">
        <div class="section-title">Investment Returns</div>
        <div class="metrics-grid">
          <div class="metric-card">
            <div class="metric-label">Annual Cash Flow</div>
            <div class="metric-value ${metrics.annualCashFlow >= 0 ? 'positive' : 'negative'}">${formatCurrency(metrics.annualCashFlow)}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Net Operating Income</div>
            <div class="metric-value">${formatCurrency(metrics.noi)}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Total Monthly Expenses</div>
            <div class="metric-value">${formatCurrency(metrics.monthlyExpenses)}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Property Added</div>
            <div class="metric-value" style="font-size: 16px;">${formatDate(property.created_at)}</div>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

// Download CSV file
export const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

// Print HTML report
export const printHTMLReport = (htmlContent: string): void => {
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load before printing
    setTimeout(() => {
      printWindow.print();
      
      // Close window after print dialog is handled (give user time to print/cancel)
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    }, 750);
  } else {
    throw new Error('Pop-up blocked. Please allow pop-ups for this site to export reports.');
  }
};

// Generate filename with timestamp
export const generateFilename = (prefix: string, extension: string): string => {
  const now = new Date();
  const timestamp = now.toISOString().split('T')[0]; // YYYY-MM-DD format
  return `${prefix}_${timestamp}.${extension}`;
};